import type { ReactNode } from 'react';
import { AppointmentDateField } from '../../../../shared/AppointmentDateField';
import { NumberInput } from './DmControlPrimitives';
import { OneLineNotice } from './OneLineNotice';
import {
    MAX_ALIGNER,
    MAX_INTERVAL,
    initialDraft,
    nextScanChanged,
    nextScanEditable,
    settingsEditable,
    touchedSettings,
    type DmBaseline,
    type DmDraft,
    type DraftErrors,
    type DraftPatch
} from './dmControlDraft';
import { formatYmd, todayYmd } from './dmControlUtils';

const ERROR_FIELD_LABEL: Record<keyof DraftErrors, string> = {
    nextScan: 'Next scan',
    interval: 'Scan every',
    noGo: 'NO-GO scan',
    aligner: 'Aligner'
};

function ChangedDot() {
    return (
        <span
            className="h-1.5 w-1.5 rounded-full bg-[#007E87]"
            title="Unsaved change"
            aria-hidden
        />
    );
}

interface RowProps {
    label: string;
    htmlFor?: string;
    changed: boolean;
    children: ReactNode;
}

// Fixed height and centred, so no field state or action moves the rows.
function Row({ label, htmlFor, changed, children }: RowProps) {
    return (
        <div className="grid h-[58px] grid-cols-[96px_minmax(0,1fr)] items-center gap-3">
            <label
                htmlFor={htmlFor}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500"
            >
                {label}
                {changed && <ChangedDot />}
            </label>
            <div className="flex min-w-0 items-center">{children}</div>
        </div>
    );
}

function DaysInput({
    id,
    value,
    onChange,
    max,
    hint
}: {
    id: string;
    value: string;
    onChange: (value: string) => void;
    max: number;
    /** After "days", e.g. "after a NO-GO". */
    hint?: string;
}) {
    return (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
            <NumberInput id={id} value={value} onChange={onChange} min={1} max={max} />
            <span className="text-[13px] text-gray-500">days{hint ? ` ${hint}` : ''}</span>
        </div>
    );
}

function ReadOnly({ children, muted = false }: { children: ReactNode; muted?: boolean }) {
    return (
        <p className={`truncate text-[13px] ${muted ? 'text-gray-400' : 'text-gray-700'}`}>
            {children}
        </p>
    );
}

// One line under the rows, in the gap above the next block, so an invalid field never adds height.
function SettingsNotice({ errors }: { errors: DraftErrors }) {
    const fieldErrors = (Object.keys(errors) as (keyof DraftErrors)[])
        .filter((key) => errors[key])
        .map((key) => `${ERROR_FIELD_LABEL[key]}: ${errors[key]}`)
        .join(' · ');
    return (
        <OneLineNotice message={fieldErrors || null} className="absolute inset-x-0 top-full mt-1" />
    );
}

interface MonitoringSettingsProps {
    base: DmBaseline;
    draft: DmDraft;
    errors: DraftErrors;
    set: (patch: DraftPatch) => void;
}

function nextScanField(
    base: DmBaseline,
    draft: DmDraft,
    errors: DraftErrors,
    set: MonitoringSettingsProps['set']
) {
    if (nextScanEditable(base)) {
        return (
            <div className="w-56 max-w-full">
                <AppointmentDateField
                    id="dm-next-scan"
                    value={draft.nextScan}
                    onChange={(nextScan) => set({ nextScan })}
                    todayIso={todayYmd()}
                    minDate={todayYmd()}
                    invalid={!!errors.nextScan}
                />
            </div>
        );
    }
    if (base.status === 'stopped') return <ReadOnly muted>None</ReadOnly>;
    if (base.status === 'not_started') return <ReadOnly muted>Set by the quickstart</ReadOnly>;
    return (
        <ReadOnly>
            {formatYmd(base.resumeOn)} <span className="text-gray-400">· on the resume date</span>
        </ReadOnly>
    );
}

// The right of the monitoring panel: the schedule and the aligner, editable while DM runs.
export function MonitoringSettings({ base, draft, errors, set }: MonitoringSettingsProps) {
    const notStarted = base.status === 'not_started';
    const editable = settingsEditable(base);
    const initial = initialDraft(base);
    const current = Number(draft.current);
    const total = Number(draft.total);

    return (
        <div className="relative min-w-0">
            <div className="divide-y divide-gray-100">
                <Row
                    label="Next scan"
                    htmlFor="dm-next-scan"
                    changed={nextScanChanged(base, draft)}
                >
                    {nextScanField(base, draft, errors, set)}
                </Row>
                <Row
                    label="Scan every"
                    htmlFor="dm-interval"
                    changed={editable && Number(draft.interval) !== Number(initial.interval)}
                >
                    {editable ? (
                        <DaysInput
                            id="dm-interval"
                            value={draft.interval}
                            onChange={(interval) => set({ interval })}
                            max={MAX_INTERVAL}
                        />
                    ) : notStarted ? (
                        <ReadOnly muted>Set by the quickstart</ReadOnly>
                    ) : (
                        <ReadOnly muted>Every {base.interval ?? '?'} days</ReadOnly>
                    )}
                </Row>
                {base.needsNoGo && (
                    <Row
                        label="NO-GO scan"
                        htmlFor="dm-no-go"
                        changed={editable && Number(draft.noGo) !== Number(initial.noGo)}
                    >
                        {editable ? (
                            <DaysInput
                                id="dm-no-go"
                                value={draft.noGo}
                                onChange={(noGo) => set({ noGo })}
                                max={MAX_INTERVAL - 1}
                                hint="after a NO-GO"
                            />
                        ) : (
                            <ReadOnly muted>{base.noGo ?? '?'} days after a NO-GO</ReadOnly>
                        )}
                    </Row>
                )}
                {base.tracksAligners && (
                    <Row
                        label="Aligner"
                        htmlFor="dm-aligner-current"
                        changed={editable && touchedSettings(base, draft).aligner}
                    >
                        {editable ? (
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                                <NumberInput
                                    id="dm-aligner-current"
                                    value={draft.current}
                                    onChange={(value) => set({ current: value })}
                                    min={1}
                                    max={MAX_ALIGNER}
                                />
                                <span className="text-[13px] text-gray-500">of</span>
                                <NumberInput
                                    value={draft.total}
                                    onChange={(value) => set({ total: value })}
                                    min={1}
                                    max={MAX_ALIGNER}
                                    label="Total aligners"
                                />
                                {total >= current && current > 0 && (
                                    <span className="ml-1 text-xs text-gray-500">
                                        {total - current} to go
                                    </span>
                                )}
                            </div>
                        ) : (
                            <ReadOnly muted>
                                {notStarted
                                    ? 'Available once monitoring starts'
                                    : `#${base.current ?? '?'} of ${base.total ?? '?'}`}
                            </ReadOnly>
                        )}
                    </Row>
                )}
            </div>
            <SettingsNotice errors={errors} />
        </div>
    );
}
